import React, { useState } from 'react';
import {
  Modal, Upload, Button, Table, Tag, Alert, Typography,
  Space, Divider, Statistic, Row, Col, Tooltip, Switch, message
} from 'antd';
import {
  InboxOutlined, CheckCircleOutlined,
  WarningOutlined, BankOutlined, FileExcelOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Dragger } = Upload;
const { Text } = Typography;

export default function BankReconcile({ visible, onClose, refreshList }) {
  const [fileList, setFileList]       = useState([]);
  const [scanning, setScanning]       = useState(false);
  const [confirming, setConfirming]   = useState(false);
  const [scanResult, setScanResult]   = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [alsoVerify, setAlsoVerify]   = useState(false);

  const token   = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}` };

  const handleReset = () => {
    setFileList([]);
    setScanResult(null);
    setSelectedIds([]);
    setAlsoVerify(false);
  };

  const handleClose = () => { handleReset(); onClose(); };

  const handleScan = async () => {
    if (!fileList.length) { message.warning('Please select an Excel statement file first.'); return; }
    setScanning(true);
    setScanResult(null);
    setSelectedIds([]);
    try {
      const formData = new FormData();
      formData.append('statement', fileList[0].originFileObj);
      const res = await axios.post(`${baseUrl}/payments/reconcile-statement`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.status) {
        setScanResult(res.data);
        // Auto-select rows that are not yet checked AND amount matches
        const autoSelect = res.data.matched
          .filter(r => !r.already_checked && r.amount_match)
          .map(r => r.payment_id);
        setSelectedIds(autoSelect);
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedIds.length) { message.warning('No payments selected.'); return; }
    setConfirming(true);
    try {
      const confirmedIds = selectedIds.map(id => Number(id));
      const res = await axios.post(
        `${baseUrl}/payments/reconcile-confirm`,
        { payment_ids: confirmedIds, also_verify: alsoVerify },
        { headers }
      );
      if (res.data.status) {
        message.success(res.data.message);
        // Update scan result in-place so table reflects new state immediately
        setScanResult(prev => ({
          ...prev,
          matched: prev.matched.map(r =>
            confirmedIds.includes(Number(r.payment_id))
              ? {
                  ...r,
                  already_checked: true,
                  is_payment_check: 'checked',
                  ...(alsoVerify ? { already_verified: true, payment_virify: 'verified' } : {}),
                }
              : r
          ),
        }));
        setSelectedIds([]);
        // Refresh the background payment list without closing the modal
        if (refreshList) refreshList();
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Confirmation failed.');
    } finally {
      setConfirming(false);
    }
  };

  const uploadProps = {
    accept: '.xlsx,.xls',
    maxCount: 1,
    fileList,
    beforeUpload: () => false,
    onChange: ({ fileList: fl }) => setFileList(fl),
    onRemove: () => { setFileList([]); setScanResult(null); setSelectedIds([]); },
  };

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys) => setSelectedIds(keys),
    getCheckboxProps: (record) => ({
      // Disable row if it's already both checked AND verified — nothing left to do
      disabled: record.already_checked && record.already_verified,
    }),
  };

  const columns = [
    {
      title: 'Member',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.phone_no}</Text>
        </div>
      ),
    },
    {
      title: 'UTR / Ref No.',
      dataIndex: 'utr_number',
      key: 'utr_number',
      width: 150,
      render: (utr) => <Text code style={{ fontSize: 11 }}>{utr || '—'}</Text>,
    },
    {
      title: 'Amt (Payment)',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      width: 110,
      render: (amt) => `₹${Number(amt).toLocaleString('en-IN')}`,
    },
    {
      title: 'Amt (Statement)',
      dataIndex: 'stmt_amount',
      key: 'stmt_amount',
      width: 120,
      render: (amt, record) => (
        <Space size={4}>
          <span>₹{Number(amt).toLocaleString('en-IN')}</span>
          {record.amount_match
            ? <Tooltip title="Amount matches"><CheckCircleOutlined style={{ color: '#52c41a' }} /></Tooltip>
            : <Tooltip title="Amount mismatch — verify manually"><WarningOutlined style={{ color: '#faad14' }} /></Tooltip>}
        </Space>
      ),
    },
    {
      title: 'Stmt Date',
      dataIndex: 'stmt_date',
      key: 'stmt_date',
      width: 100,
      render: (d) => d || '—',
    },
    {
      title: 'Match',
      dataIndex: 'match_type',
      key: 'match_type',
      width: 70,
      render: (t) => (
        <Tag color={t === 'utr' ? 'blue' : 'orange'} style={{ fontSize: 11 }}>
          {t === 'utr' ? 'UTR' : 'Phone'}
        </Tag>
      ),
    },
    {
      title: 'Check Pay',
      key: 'is_payment_check',
      width: 95,
      render: (_, record) =>
        record.already_checked
          ? <Tag color="green" style={{ fontSize: 11 }}>Checked</Tag>
          : <Tag color="red"   style={{ fontSize: 11 }}>Unchecked</Tag>,
    },
    {
      title: 'Pay Status',
      key: 'payment_virify',
      width: 95,
      render: (_, record) =>
        record.already_verified
          ? <Tag icon={<SafetyCertificateOutlined />} color="success" style={{ fontSize: 11 }}>Verified</Tag>
          : <Tag color="warning" style={{ fontSize: 11 }}>Pending</Tag>,
    },
  ];

  const checkedCount  = scanResult?.matched?.filter(r => r.already_checked).length  ?? 0;
  const verifiedCount = scanResult?.matched?.filter(r => r.already_verified).length ?? 0;
  const pendingCheck  = scanResult?.matched?.filter(r => !r.already_checked).length ?? 0;

  const confirmLabel = alsoVerify
    ? `Check + Verify Selected (${selectedIds.length})`
    : `Mark Checked (${selectedIds.length})`;

  return (
    <Modal
      title={<Space><BankOutlined style={{ color: '#1890ff' }} /><span>Bank Statement Reconciliation</span></Space>}
      open={visible}
      onCancel={handleClose}
      width={1020}
      footer={null}
      destroyOnClose
    >
      {/* Upload step */}
      {!scanResult && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Upload SBI Excel Bank Statement"
            description="Download the Excel (.xlsx) statement from SBI Net Banking → Accounts → Account Statement. The system extracts UTR/ref numbers from each credit entry and auto-matches to pending payments."
          />
          <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
            <p className="ant-upload-drag-icon">
              <FileExcelOutlined style={{ color: '#52c41a', fontSize: 40 }} />
            </p>
            <p className="ant-upload-text">Click or drag SBI Excel statement here</p>
            <p className="ant-upload-hint">Supports .xlsx and .xls — max 10 MB</p>
          </Dragger>
          <div style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<InboxOutlined />}
              loading={scanning}
              disabled={!fileList.length}
              onClick={handleScan}
              size="large"
            >
              {scanning ? 'Scanning…' : 'Scan & Match'}
            </Button>
          </div>
        </>
      )}

      {/* Results step */}
      {scanResult && (
        <>
          {/* Summary stats */}
          <Row gutter={24} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic title="Credit Rows in Statement" value={scanResult.total_credit_rows} />
            </Col>
            <Col span={6}>
              <Statistic title="Matched Payments" value={scanResult.matched_count} valueStyle={{ color: '#1890ff' }} />
            </Col>
            <Col span={6}>
              <Statistic title="Pending Check" value={pendingCheck} valueStyle={{ color: '#faad14' }} />
            </Col>
            <Col span={6}>
              <Statistic title="Already Verified" value={verifiedCount} valueStyle={{ color: '#52c41a' }} />
            </Col>
          </Row>

          <Alert
            type={pendingCheck > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 12 }}
            message={scanResult.message}
            description={
              pendingCheck > 0
                ? 'Select the rows you want to process, choose an action below, then confirm.'
                : `All matched payments are already checked (${checkedCount} checked, ${verifiedCount} verified).`
            }
          />

          <Table
            rowKey="payment_id"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={scanResult.matched}
            pagination={{ pageSize: 10, size: 'small' }}
            size="small"
            bordered
            scroll={{ x: 860 }}
          />

          <Divider />

          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Button onClick={handleReset}>Upload Another Statement</Button>

            <Space size={16} wrap>
              {/* Two-step toggle */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: alsoVerify ? '#f6ffed' : '#fff7e6',
                border: `1px solid ${alsoVerify ? '#b7eb8f' : '#ffd591'}`,
                borderRadius: 8, padding: '6px 12px',
              }}>
                <Switch
                  checked={alsoVerify}
                  onChange={setAlsoVerify}
                  size="small"
                  style={{ backgroundColor: alsoVerify ? '#52c41a' : undefined }}
                />
                <span style={{ fontSize: 13, fontWeight: 500, color: alsoVerify ? '#389e0d' : '#d46b08' }}>
                  {alsoVerify
                    ? '✓ Check Pay + Verify'
                    : 'Check Pay only (no Verify yet)'}
                </span>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                  {alsoVerify
                    ? 'Will set: CheckPay → Checked, Pay Status → Verified'
                    : 'Will set: CheckPay → Checked  (Pay Status unchanged)'}
                </div>
                <Button
                  type="primary"
                  icon={alsoVerify ? <SafetyCertificateOutlined /> : <CheckCircleOutlined />}
                  loading={confirming}
                  disabled={!selectedIds.length}
                  onClick={handleConfirm}
                  style={alsoVerify ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
                >
                  {confirmLabel}
                </Button>
              </div>
            </Space>
          </div>
        </>
      )}
    </Modal>
  );
}
